import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CaptchaField from "@/components/CaptchaField";
import EditLinkDialog from "@/components/EditLinkDialog";

const CATEGORIES = ["Comida", "Transporte", "Proyecto del Hogar", "Cuidado de Niños", "Petición de Oración", "Otro"];

export default function PostNeedDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", category: "",
    title: "", description: "", needed_date: "",
  });
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, answer: "" });
  const [loading, setLoading] = useState(false);
  const [editLink, setEditLink] = useState(null);

  const reset = () => {
    setForm({ name: "", phone: "", email: "", category: "", title: "", description: "", needed_date: "" });
    setCaptcha({ a: 0, b: 0, answer: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.title || !form.description || !form.needed_date) {
      toast.error("Por favor complete todos los campos requeridos");
      return;
    }
    if (captcha.answer === "" || captcha.answer === null) {
      toast.error("Por favor responda la verificación");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/needs", { ...form, captcha });
      const url = `${window.location.origin}/edit/${data.edit_token}`;
      setEditLink(url);
      reset();
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="post-need-dialog"
          className="max-w-2xl max-h-[92vh] overflow-y-auto bg-white border-2 border-[#D1C9C1]"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl sm:text-3xl text-[#1A1818]">
              Publicar una Necesidad
            </DialogTitle>
            <DialogDescription className="text-base text-[#4A4646]">
              Comparta su necesidad con el grupo. Le daremos un enlace privado para administrarla.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5 mt-2">
            <div>
              <Label htmlFor="name" className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                Su Nombre <span className="text-[#8A3A33]">*</span>
              </Label>
              <Input
                id="name" data-testid="post-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12 text-base border-2 border-[#D1C9C1]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                  Teléfono (opcional)
                </Label>
                <Input
                  id="phone" data-testid="post-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-12 text-base border-2 border-[#D1C9C1]"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                  Correo electrónico (opcional)
                </Label>
                <Input
                  id="email" data-testid="post-email" type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 text-base border-2 border-[#D1C9C1]"
                />
              </div>
            </div>

            <div>
              <Label className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                Categoría <span className="text-[#8A3A33]">*</span>
              </Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="post-category" className="h-12 text-base border-2 border-[#D1C9C1]">
                  <SelectValue placeholder="Seleccione una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="text-base py-3">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="title" className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                Título <span className="text-[#8A3A33]">*</span>
              </Label>
              <Input
                id="title" data-testid="post-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Resumen breve de la necesidad"
                className="h-12 text-base border-2 border-[#D1C9C1]"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                Descripción <span className="text-[#8A3A33]">*</span>
              </Label>
              <Textarea
                id="description" data-testid="post-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder="Describa la necesidad con detalles útiles"
                className="text-base border-2 border-[#D1C9C1] min-h-[120px]"
              />
            </div>

            <div>
              <Label htmlFor="needed_date" className="text-base font-semibold text-[#1A1818] mb-1.5 block">
                Fecha en que se necesita la ayuda <span className="text-[#8A3A33]">*</span>
              </Label>
              <Input
                id="needed_date" data-testid="post-needed-date" type="date"
                value={form.needed_date}
                onChange={(e) => setForm({ ...form, needed_date: e.target.value })}
                className="h-12 text-base border-2 border-[#D1C9C1]"
              />
            </div>

            <CaptchaField value={captcha} onChange={setCaptcha} />

            <DialogFooter className="gap-3 pt-2">
              <Button
                type="button" variant="outline"
                onClick={() => onOpenChange(false)}
                className="h-12 px-6 text-base border-2 border-[#D1C9C1]"
              >
                Cancelar
              </Button>
              <Button
                type="submit" disabled={loading}
                data-testid="post-submit"
                className="h-12 px-6 text-base font-semibold bg-[#8A3A33] hover:bg-[#6B2B25] text-white"
              >
                {loading ? "Publicando..." : "Publicar Necesidad"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <EditLinkDialog
        link={editLink}
        onClose={() => {
          setEditLink(null);
          onOpenChange(false);
        }}
      />
    </>
  );
}
